import Vue from "vue";
import Vuex from "vuex";
import * as _ from "lodash";
import * as Api from "../../pages/modules/cirt/api/networkData"
import {
    ROLES,
    WORKER_STATUSES,
    BUSY_TIMEOUT,
    DEFAULT_ROUND_TIMER_MINS,
    DEFAULT_BREAK_TIMER_MINS,
    NODE_STATUS_PRIORITY,
    ROLE_CODES,
    ROLE_MAP,
    CONTRACTORS
} from "../../pages/modules/cirt/constants";
import moment from "moment";
import { Session } from "../../pages/modules/cirt/classes/Session";
import { 
    ACTIONS_PER_ROUND_PER_ROLE, 
    PIO, 
    SCADA, 
    SOC, 
    IT 
} from "../../pages/modules/cirt/classes/constants";
import { Node } from "../../pages/modules/cirt/classes/Node";
import axios from "axios";
import { AttackManager } from "../../pages/modules/cirt/classes/AttackManager";
import { NodeData } from "../../pages/modules/cirt/classes/NodeData";

// When rounds are completed, current round is -1 from the info endpoint
const ROUND_COMPLETED = -1;

Vue.use(Vuex);

let VueInstance = new Vue();


export const state = {
    session: {},
    startingPlaytest: false,
    playtestStarterId: null,
    playtestConfig: {},
    graphData: null,
    cachedNodePositions: null,
    loading: false,
    selectedNode: null,
    selectedGroup: null,
    forceLayoutEnabled: false,
    role: null,
    gameExists: false,
    info: {
        started: false,
        round: 0,
        isInBreak: false,
        lossIncurred: 0,
        roundStartTimeStamp: new Date().toISOString(),
        finalResults: "",
        roles: {
            pio: { actionsLeft: 3 },
            it: { actionsLeft: 3 },
            scada: { actionsLeft: 3 },
            soc: { actionsLeft: 3 }
        },
        percentNodesCaughtSimple: 0,
    },
    roundDuration: 5,
    breakDuration: 2,
    breakEvents: [],

    drillInfo: {
        drillRoundMin: 7,
        afterDrillEvents: null,
        drillRoundLobbyMessage: "",
        liveRoundLobbyMessage: "",
        confirmDrillRestartMsg: "",
        confirmDrillEndMsg: ""
    },
    videos: {
        round2BadProgress: "",
        round3BadProgress: "",
        round2AverageProgress: "",
        round3AverageProgress: "",
        round2GoodProgress: "",
        round3GoodProgress: "",
        captions: {
            round2BadProgress: "",
            round3BadProgress: "",
            round2AverageProgress: "",
            round3AverageProgress: "",
            round2GoodProgress: "",
            round3GoodProgress: ""
        }
    },
    roundAttacks: {
        2: 2,
        3: 1
    },

    confirmForceStartMessage: "",
    finalReportModalMessage: "Thanks for responding to this attack so soon after your training! I've provided a quick assessment of your actions below. After you review it, check for more details and next steps in an email from Penny Davis in HR. We stalled R0b1nh00d's advance on our systems for now, but not before they broadcast this city-wide tweet: ",

    // update related
    checkingForUpdate: false,
    previousData: null,
    // modals
    gameOverModalOpen: false,
    nextRoundModalOpen: false,

    // number of ongoing investigations/shutoff actions
    // this could be greater than 1 to allow parallel investigations/shutoff actions
    workersBusy: 0,
    //Default Round timers
    roundTimers: {
        1: 5,
        2: 7,
        3: 7,
        4: 5
    },
    successEvent: null,
    failEvent: null,
};
export const getters = {
    gameFinished: state => {
        return state.info.round === ROUND_COMPLETED;
    },
    roundEndTime: (state, getters) => {
        // returns timestamp for end of round
        if (getters.outOfActions) {
            return moment(Date.now()).add(10, "seconds").toISOString()
        }
        else {
            let finishTime = moment(state.info.roundStartTimeStamp).add(
                state.info.drill ? state.drillInfo.drillRoundMin : (state.roundTimers[state.info.round] || DEFAULT_ROUND_TIMER_MINS),
                "minutes"
            ).toISOString();
    
            return finishTime;
        }
    },
    outOfActions: (state) => {
        let outOfActions = true
        for (let key of Object.keys(state.session.roles)) {
            if (state.session.roles[key].actionsLeftThisRound > 0) {
                outOfActions = false
                break
            }
        }
        return outOfActions
    },
    workers: (state, getters) => {
        if (getters.isAdmin) return;
        let actionsLeft = state.info.roles[getters.role].actionsLeft;
        let workersBusy = state.workersBusy;
        let workers = [];
        for (let i = 0; i < ACTIONS_PER_ROUND_PER_ROLE; ++i) {
            let newWorker = {
                name: CONTRACTORS[getters.role][i]["name"],
                description: CONTRACTORS[getters.role][i]["description"],
                status:
                    actionsLeft > 0
                        ? WORKER_STATUSES.AVAILABLE
                        : workersBusy > 0
                            ? WORKER_STATUSES.BUSY
                            : "unavailable"
            };
            if (newWorker.status === WORKER_STATUSES.BUSY) workersBusy -= 1;
            if (newWorker.status === "available") actionsLeft -= 1;

            workers.push(newWorker);
        }
        return workers;
    },
    groupFilteredNodes: state => {
        return state.graphData.nodes.filter(
            n => !state.selectedGroup || n.group === state.selectedGroup
        );
    },
    groups: state => Array.from(new Set(state.graphData.nodes.map(n => n.group))),

    allNotifications: (state, getters) => {
        let notifications = [];
        for (let node of state.graphData.nodes.filter(
            n => n.status !== "healthy"
        )) {
            if (getters.isAdmin) {
                // show all notifications
                for (const [key, value] of Object.entries(node.notifications)) {
                    let lastNotification =
                        node.notifications[key][node.notifications[key].length - 1];
                    lastNotification &&
                        notifications.push({
                            content: lastNotification,
                            group: null,
                            node: node,
                            role: key,
                            status: node.status
                        });
                }
            } else {
                let lastNotification =
                    node.notifications[getters.role][node.notifications[getters.role].length - 1];
                lastNotification &&
                    notifications.push({
                        content: lastNotification,
                        group: null,
                        node: node,
                        role: getters.role,
                        status: node.status
                    });
            }
        }
        return notifications;
    },
    roleNotifications: (state, getters) => {
        return getters.allNotifications
            .filter(n => getters.isAdmin || n.role === getters.role)
            .sort(
                (a, b) =>
                    NODE_STATUS_PRIORITY[b.node.status] -
                    NODE_STATUS_PRIORITY[a.node.status]
            );
    },

    isAdmin: (state, getters) => getters.role && getters.role === "admin",
    role: (state, getters, rootState) => ROLE_MAP[rootState.group.role.name],
    sessionId: (state, getters, rootState) => rootState.group.group.id,
    shouldUpdate: (state, getters, rootState, rootGetters) => {
        let userRole = getters.role;
        let groupOnlineRoles = rootGetters['group/onlineRoles'];
        let onlineRoles = {}
        for (let role in groupOnlineRoles) {
            onlineRoles[ROLE_MAP[role]] = groupOnlineRoles[role];
        }
        return ((userRole === PIO) || 
                (userRole === SCADA && !onlineRoles[PIO]) ||
                (userRole === SOC && !onlineRoles[PIO] && !onlineRoles[SCADA]) ||
                (userRole === IT && !onlineRoles[PIO] && !onlineRoles[SCADA] && !onlineRoles[SOC]))
    }
}
export const actions = {
    toggleForceLayout(context) {
        context.commit("TOGGLE_FORCE_LAYOUT");
    },
    async updateCirt(context, payload) {
        await axios.post('/api/cirt/updateCirt', {
            params: {
                groupId: context.rootState.group.group.id,
                actionType: payload.actionType,
                data: payload.data
            }
        })
    },
    startBreak(context) {
        if (context.state.info.round == context.state.info.totalRounds && !context.state.info.drill) {
            context.dispatch('advanceRound')
        }
        else if (context.getters.shouldUpdate) {
            context.dispatch('forceStartBreak')
        }
    },
    async updateData(context, payload) {
        if (Object.keys(context.state.session).length) {
            let networkView = Api.getNetworkView(context.state.session, context.getters.role)
            let info = await Api.getInfo(context.state.session)

            context.commit("SET_DATA", networkView);
            context.commit("SET_INFO", info);
            context.dispatch("setGameExists", info);
            if (context.getters.gameFinished) {
                context.commit("SET_GAME_OVER_MODAL_OPEN", true);
            }
        }
    },
    setGameExists(context, payload) {
        if (payload.success == false) {
            context.state.gameExists = false;
        }
        context.state.gameExists = true;
    },
    setSelectedNode(context, payload) {
        context.commit("SET_SELECTED_NODE", payload);
    },
    setSelectedGroup(context, payload) {
        context.commit("SET_SELECTED_GROUP", payload);
    },
    investigate(context, payload) {
        // TODO: confirm whether role names in the PCS are same
        let role = context.getters.role;
        let actionsLeft = context.state.info.roles[role].actionsLeft;
        if (actionsLeft === 0) {
            VueInstance.$toasted.error(
                "No contractor is available to perform this action."
            );
            return;
        }
        context.commit("SET_WORKERS_BUSY", 1);
        try {
            Api.investigate(context.state.session, payload, role)
            context.dispatch("updateData");
            setTimeout(() => {
                context.commit("SET_WORKERS_BUSY", 0);
            }, BUSY_TIMEOUT);
        } catch (err) {
            VueInstance.$toasted.show(err.message);
        }
    },
    notify(context, payload) {
        let role = context.getters.role;
        let actionsLeft = context.state.info.roles[role].actionsLeft;
        if (actionsLeft === 0) {
            VueInstance.$toasted.error(
                "No contractor is available to perform this action."
            );
            return;
        }
        context.commit("SET_WORKERS_BUSY", 1);
        try {
            Api.notify(context.state.session, payload, role)
            context.dispatch("updateData");
            setTimeout(() => {
                context.commit("SET_WORKERS_BUSY", 0);
                VueInstance.$toasted.info("Node notified");
            }, BUSY_TIMEOUT);
        } catch (err) {
            VueInstance.$toasted.show(err.message);
        }
    },
    shutoff(context, payload) {
        let role = context.getters.role;

        let actionsLeft = context.state.info.roles[role].actionsLeft;
        if (actionsLeft === 0) {
            VueInstance.$toasted.error(
                "No contractor is available to perform this action."
            );
            return;
        }
        VueInstance.$toasted.info("Shutting down...");
        context.commit("SET_WORKERS_BUSY", 1);

        try {
            Api.shutoff(context.state.session, payload, context.getters.role)
            context.dispatch("updateData");
            setTimeout(() => {
                context.commit("SET_WORKERS_BUSY", 0);
                VueInstance.$toasted.info("Node successfully shut down");
            }, BUSY_TIMEOUT);
        } catch (err) {
            VueInstance.$toasted.show(err.message);
        }
    },

    isolate(context, payload) {
        let role = context.getters.role;

        let actionsLeft = context.state.info.roles[role].actionsLeft;
        if (actionsLeft === 0) {
            VueInstance.$toasted.error(
                "No contractor is available to perform this action."
            );
            return;
        }
        VueInstance.$toasted.info("Isolating...");
        context.commit("SET_WORKERS_BUSY", 1);

        try {
            Api.isolate(context.state.session, payload, context.getters.role)
            context.dispatch("updateData");
            setTimeout(() => {
                context.commit("SET_WORKERS_BUSY", 0);
                VueInstance.$toasted.info("Node successfully isolated");
            }, BUSY_TIMEOUT);
        } catch (err) {
            VueInstance.$toasted.show(err.message);
        }
    },

    forceStartBreak(context) {
        context.state.session.startBreak()
        context.dispatch('updateData')
    },

    advanceRound(context) {
        try {
            if (context.getters.shouldUpdate) {
                Api.advanceRound(context.state.session)
                context.dispatch("updateData");
            }
        } catch (err) {
            VueInstance.$toasted.show(err.message);
        }
    },
    makeRoleReady(context, payload) {
        try {
            Api.makeRoleReady(context.state.session, payload.role)
        } catch (err) {
            VueInstance.$toasted.show(err.message);
        }
        context.dispatch("updateData");
    },
    forceGameStart(context, payload) {
        try {
            Api.forceGameStart(context.state.session)
        } catch (err) {
            VueInstance.$toasted.show(err.message);
        }
        context.dispatch("updateData");
    },
    setRole(context, payload) {
        if (
            ROLES.includes(payload.role) &&
            payload.accessCode === ROLE_CODES[payload.role]
        ) {
            context.commit("SET_ROLE", payload.role);
            VueInstance.$toasted.success("Logged In");
            if (payload.role === "admin") {
                router.push("/admin");
            } else {
                router.push("/");
            }
        } else {
            VueInstance.$toasted.error("Invalid Role or Access Code");
        }
    },

    async startPlaytest(context, payload) {
        if (context.getters.shouldUpdate) {
            let response = await axios.post('/api/cirt/checkForSession', {
                params: {
                    groupId: context.getters.sessionId
                }
            })
            let sessionExists = response.data
            if (!sessionExists || payload.force) {
                context.state.startingPlaytest = true
                context.state.playtestStarterId = context.rootState.auth.user.id
                context.state.playtestConfig = payload
                context.dispatch('updateCirt', { 
                    actionType: 'startPlaytest', 
                    data: { 
                        startingPlaytest: context.state.startingPlaytest, 
                        playtestStarterId: context.state.playtestStarterId, 
                        playtestConfig: context.state.playtestConfig
                    } 
                })
                payload.round_duration = (payload.drill) ? context.state.drillInfo.drillRoundMin * 60 : context.state.roundDuration * 60
                payload.break_duration = context.state.breakDuration * 60 
                payload.round_attacks = context.state.roundAttacks
                let session = await Api.beginGame(context.getters.sessionId, payload);
                context.commit("SET_CIRT_SESSION", session);
                context.dispatch("updateData");
                context.state.startingPlaytest = false
                context.state.playtestStarterId = null
                context.state.playtestConfig = null
                context.dispatch('updateCirt', { 
                    actionType: 'startPlaytest', 
                    data: { 
                        startingPlaytest: context.state.startingPlaytest, 
                        playtestStarterId: context.state.playtestStarterId, 
                        playtestConfig: context.state.playtestConfig 
                    } 
                })
            }
        }
    },

    populateFromDB(context, payload) {
        Api.getCirtModuleContent().then( res => {
            context.commit("SET_CIRT_MODULE_INFO", res.data['settings'])
        })
    },

    async refreshDataFromDB(context) {
        let res = await Api.getGroupSettings(context.rootState.group.group.id)
        if (Object.keys(res.data.content).length) {
            context.commit("SET_CIRT_SESSION", res.data.content)
            context.dispatch("updateData");
        }
    },
    async updateSession(context, payload) {
        if (!Object.keys(context.state.session).length && payload.actionType !== 'resetState') {
            await context.dispatch('refreshDataFromDB')
        }
        if (payload.action_type === "resetState") {
            context.dispatch('resetState')
        } 
        else if (payload.action_type === "restartDrill") {
            context.dispatch('startPlaytest', payload.data)
        }
        else {
            context.commit("UPDATE_SESSION", payload)
            context.dispatch("updateData");
        }
    },
    saveToDb(context, payload) {
        Api.saveSessionToDb(context.rootState.group.group.id, context.state.session)
    },
    resetState(context) {
        context.commit('RESET_STATE')
    }
}
export const mutations = {
    SET_DATA(state, data) {
        let nodes = data.nodes;
        let links = data.edges;
        
        if (data.success != false) {
            state.graphData = { nodes: (Object.entries(nodes).map(n => n[1])), links };
        }
        // to check for updates
        state.previousData = data;
    },
    UPDATE_SESSION(state, payload) {
        let actionType = payload.action_type
        let data = payload.data

        switch(actionType) {
            case "buildSession": {
                let session = new Session()
                session.jsonDeserialize(data)
                state.session = session
                break;
            }
            case "startGame": {
                state.session.roundStartTimeStamp = data.roundStartTimeStamp
                state.session.started = data.started
                break;
            }
            case "startPlaytest": {
                state.startingPlaytest = data.startingPlaytest
                state.playtestStarterId = data.playtestStarterId
                state.playtestConfig = data.playtestConfig
                break;
            }
            case "isolate": {
                let node = Node.jsonDeserialize(data.node)
                let caught = data.caught
                let role = data.role
                let actionsLeft = data.actionsLeft

                let allEdges = state.session.storage.getNodeEdges(node.getData().name);
                for (let edge of allEdges) {
                    state.session.storage.disconnectEdge(edge.getSource(), edge.getDestination());
                    if (!state.session.disconnectedEdges.includes(edge.getSource() + "-" + edge.getDestination())) {
                        state.session.disconnectedEdges.push(edge.getSource() + "-" + edge.getDestination());
                    }
                    state.session.notificationManager.addEdgeDisconnectionNotificationToNodes(state.session.storage, edge.getSource(), edge.getDestination(), state.session.round);
                }

                state.session.storage.graph.nodes[node.getData().name] = node
                if (caught) state.session.numNodesCaught++
                state.session.roles[role].actionsLeftThisRound = actionsLeft
                break;
            }
            case "notify": {
                let notifyImpact = data.notifyImpact
                let role = data.role
                let actionsLeft = data.actionsLeft

                state.session.notifyImpact = notifyImpact
                state.session.roles[role].actionsLeftThisRound = actionsLeft
                break;
            }
            case "shutoff": {
                let node = Node.jsonDeserialize(data.node)
                let caught = data.caught
                let role = data.role
                let actionsLeft = data.actionsLeft

                state.session.storage.graph.nodes[node.getData().name] = node
                if (caught) state.session.numNodesCaught++
                if(!state.session.shutoffNodes.includes(node.getData().name)) state.session.shutoffNodes.push(node.getData().name)
                state.session.roles[role].actionsLeftThisRound = actionsLeft
                break;
            }
            case "investigate": {
                let node = Node.jsonDeserialize(data.node)
                let role = data.role
                let actionsLeft = data.actionsLeft

                state.session.storage.graph.nodes[node.getData().name] = node
                state.session.roles[role].actionsLeftThisRound = actionsLeft
                break;
            }
            case "advanceRound": {
                let percentNodesCaught = data.percentNodesCaught
                let round = data.round
                let roundStartTimeStamp = data.roundStartTimeStamp
                let attackManager = data.attackManager
                let attackManagerWorstCase = data.attackManagerWorstCase
                let inBreak = data.inBreak
                let nodes = data.nodes
                let allInfectedNodes = data.allInfectedNodes
                let end = data.end
                
                state.session.storage.percentNodesCaught = percentNodesCaught
                state.session.round = round
                state.session.roundStartTimeStamp = roundStartTimeStamp
                state.session.attackManager = AttackManager.jsonDeserialize(attackManager)
                state.session.attackManagerWorstCase = AttackManager.jsonDeserialize(attackManagerWorstCase)
                state.session.storage.allInfectedNodes = allInfectedNodes
                for (let node in nodes) {
                    state.session.storage.graph.nodes[node] = Node.build(NodeData.jsonDeserialize(nodes[node].data))
                }
                
                if (!end) {
                    for (let role of Object.values(state.session.roles)) {
                        role.resetActions();
                    }
                    state.session.inBreak = inBreak
                }
                break;
            }
            case "startBreak": {
                state.session.inBreak = data.inBreak;
                break;
            }
            case "makeRoleReady": {
                state.session.roles[data.roleName].ready = data.isReady
                break;
            }
            default:
                break;
        }
    },
    TOGGLE_FORCE_LAYOUT(state) {
        state.forceLayoutEnabled = !state.forceLayoutEnabled;
    },
    SET_LOADING(state, data) {
        state.loading = data;
    },
    SET_SELECTED_NODE(state, data) {
        state.selectedNode = data;
    },
    SET_SELECTED_GROUP(state, data) {
        state.selectedGroup = data;
    },
    SET_INFO(state, data) {
        state.info = data;
    },

    SET_ROLE(state, role) {
        getters.role = role;
        if (role) {
            localStorage.setItem("role", role);
        } else {
            localStorage.removeItem("role");
        }
    },

    SET_CHECKING_FOR_UPDATE(state, value) {
        state.checkingForUpdate = value;
    },

    SET_WORKERS_BUSY(state, value) {
        state.workersBusy = value;
    },

    SET_CACHED_NODE_POSITIONS(state, payload) {
        state.cachedNodePositions = payload;
    },
    SET_GAME_OVER_MODAL_OPEN(state, payload) {
        state.gameOverModalOpen = payload;
    },
    SET_NEXT_ROUND_MODAL_OPEN(state, payload) {
        state.nextRoundModalOpen = payload;
    },
    SET_CIRT_MODULE_INFO(state, payload) {
        state.drillInfo = payload.drill
        state.confirmForceStartMessage =  payload.confirmForceStartMessage
        if (payload.finalReportModalMessage) state.finalReportModalMessage = payload.finalReportModalMessage,
        state.videos = payload.videos
        state.roundAttacks = payload.roundAttacks
        state.roundDuration = payload.roundDurationMin ?? DEFAULT_ROUND_TIMER_MINS
        state.breakDuration = payload.breakDurationMin ?? DEFAULT_BREAK_TIMER_MINS
        state.breakEvents = payload.breakEvents
        // Set round times dynamically
        for(let i = 1; i <= payload.rounds + 1; i++){
            state.roundTimers[i] = payload.roundDurationMin 
        } 
        state.successEvent = payload.successEvent
        state.failEvent = payload.failEvent
    },
    SET_CIRT_SESSION(state, payload) {
        let session = new Session();
        session.jsonDeserialize(JSON.stringify(payload));
        state.session = session;
    },
    RESET_STATE(state) {
        state.session = {}
        state.startingPlaytest = false
        state.graphData = null
        state.cachedNodePositions = null
        state.loading = false
        state.selectedNode = null
        state.selectedGroup = null
        state.forceLayoutEnabled = false
        state.role = null
        state.gameExists = false
        state.info = {
            started: false,
            round: 0,
            isInBreak: false,
            lossIncurred: 0,
            roundStartTimeStamp: new Date().toISOString(),
            finalResults: "",
            roles: {
                pio: { actionsLeft: 0 },
                it: { actionsLeft: 0 },
                scada: { actionsLeft: 0 },
                soc: { actionsLeft: 0 },
            },
            percentNodesCaughtSimple: 0,
        },

        state.gameOverModalOpen = false
        state.nextRoundModalOpen = false
        state.workersBusy = 0
    },
    CLEAR_STATE(state) {
        state.graphData = null,
        state.cachedNodePositions = null,
        state.loading = false,
        state.selectedNode = null,
        state.selectedGroup = null,
        state.forceLayoutEnabled = false,
        state.role = null,
        state.gameExists = false,
        state.info = {},
        state.drillInfo = {},
        state.videos = {},
        state.roundAttacks = {}
        state.confirmForceStartMessage = "",
        state.finalReportModalMessage = "",
        state.checkingForUpdate = false,
        state.previousData = null,
        state.gameOverModalOpen = false,
        state.nextRoundModalOpen = false,
        state.workersBusy = 0,
        state.session = {}
    }
}